import { useEffect, useMemo, useRef, useState } from "react";

export type SelectOption = { value: string; label: string };

type SearchableSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Show the search box. Defaults to automatic: only when there are more
      than 7 options — a search box is overkill for a handful of items. */
  searchable?: boolean;
  /** Classes for the closed-state trigger. Defaults to the edit-form field look. */
  className?: string;
};

const DEFAULT_TRIGGER =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink focus:outline-none";

// Diacritic-insensitive matching ("jose" finds "José").
function normalize(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export default function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Seleccionar...",
  required = false,
  disabled = false,
  searchable,
  className = DEFAULT_TRIGGER,
}: SearchableSelectProps) {
  const showSearch = searchable ?? options.length > 7;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = normalize(query);
    return options.filter((o) => normalize(o.label).includes(q));
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    function onMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
        setHighlighted(0);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  // Keep the highlighted row visible while navigating with the keyboard.
  // biome-ignore lint/correctness/useExhaustiveDependencies: highlighted drives the data-highlighted DOM query
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-highlighted="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  function close() {
    setOpen(false);
    setQuery("");
    setHighlighted(0);
  }

  function openList() {
    const idx = options.findIndex((o) => o.value === value);
    setHighlighted(idx >= 0 ? idx : 0);
    setOpen(true);
  }

  function pick(option: SelectOption) {
    onChange(option.value);
    close();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlighted]) pick(filtered[highlighted]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  }

  return (
    <div ref={rootRef} className="relative min-w-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        className={`${className} flex min-w-0 items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-black/5`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`min-w-0 truncate ${selected?.label ? "" : "text-ink/40"}`}
        >
          {selected?.label || placeholder}
        </span>
        <ChevronIcon
          className={`h-4 w-4 shrink-0 text-ink/60 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Invisible but focusable input so native form validation still enforces
          `required`; the browser focuses it on failed submit, which opens the list. */}
      {required && !disabled && (
        <input
          value={value}
          onChange={() => {}}
          onFocus={() => setOpen(true)}
          required
          tabIndex={-1}
          aria-hidden="true"
          className="absolute inset-0 -z-10 h-full w-full opacity-0"
        />
      )}

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-2 min-w-48 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-lg">
          {showSearch && (
            <div className="p-2">
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder="Buscar..."
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlighted(0);
                }}
                onKeyDown={onKeyDown}
                className="h-9 w-full rounded-full border-2 border-ink/20 bg-white px-4 font-sans text-sm font-light text-ink placeholder:text-ink/40 focus:border-steel focus:outline-none focus:ring-2 focus:ring-steel/30"
              />
            </div>
          )}
          <ul
            ref={listRef}
            className={`max-h-60 overflow-auto pb-1 ${showSearch ? "" : "pt-1"}`}
          >
            {filtered.length === 0 && (
              <li className="px-4 py-2 font-sans text-sm font-light text-ink/40">
                Sin resultados
              </li>
            )}
            {filtered.map((option, i) => (
              <li key={option.value}>
                <button
                  type="button"
                  data-highlighted={i === highlighted}
                  onClick={() => pick(option)}
                  onMouseEnter={() => setHighlighted(i)}
                  className={`block w-full px-4 py-2 text-left font-sans text-sm font-light ${
                    i === highlighted ? "bg-black/5" : ""
                  } ${option.value === value ? "font-semibold text-steel" : "text-ink"}`}
                >
                  {option.label || " "}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
    </svg>
  );
}
